import React, { useEffect, useState } from "react";
import { intl } from "dmsbasereact";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { DmsModal, havePermissions } from "dmsbasereact";

import { useHelpCenterData } from "./hooks/useHelpCenterData";
import { DmsHelpCenterManualTab } from "./DmsHelpCenterManualTab";
import { DmsHelpCenterOfflineBanner } from "./DmsHelpCenterOfflineBanner";
import { DmsHelpCenterUploadManual } from "./DmsHelpCenterUploadManual";
import { DmsHelpCenterContactTab } from "./DmsHelpCenterContactTab";
import { Permissions } from "../../constants/Permissions";

const formatDateTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

export const DmsHelpCenterOverlay = ({
  open,
  onClose,
  route,
  slug,
  language,
  onStateChange,
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const {
    data,
    loading,
    error,
    refresh,
    fromCache,
    lastUpdated,
    isOffline,
    isStale,
  } = useHelpCenterData({
    slug,
    route,
    language,
    enabled: open,
  });

  const [manualAnchorId, setManualAnchorId] = useState(null);

  const manuals = data?.manuals || [];

  useEffect(() => {
    if (!open) {
      return;
    }
    if (onStateChange) {
      onStateChange({
        isOffline,
        fromCache,
        isStale,
        lastUpdated,
      });
    }
  }, [open, isOffline, fromCache, isStale, lastUpdated, onStateChange]);

  const contextError = error && !data;
  const manualError = contextError ? error : null;

  const manualsCount = manuals.length;
  const countLabel = manualsCount > 0
    ? intl
        .get("helpCenter.manualsCount", { count: manualsCount })
        .d("manual(s) available")
    : null;

  const lastSyncLabel = lastUpdated && !error
    ? `${intl.get("helpCenter.lastSyncLabel").d("Sincronizado em")} ${formatDateTime(lastUpdated)}`
    : null;

  return (
    <>
          <DmsModal
            isVisible={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
          >
            <DmsModal.Header
              title={intl.get("helpCenter.title").d("Help Center")}
              onClose={onClose}
            />
            <DmsModal.Body
              sx={{
                maxHeight: "75vh",
                overflowY: "auto",
                px: 3,
                pb: 3,
              }}
            >
              <Stack spacing={2}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label={intl.get("helpCenter.tabs.manuals").d("Manuais")} />
                  <Tab label={intl.get("helpCenter.tabs.contactUs").d("Fale Conosco")} />
                </Tabs>

                {activeTab === 0 && (
                  <>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", md: "center" }}
                    >
                      <Box sx={{ flex: 1 }} />
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      >
                        {havePermissions([Permissions.CAN_UPLOAD_MANUAL]) && (
                          <Tooltip title={intl.get("DmsHelpCenterUploadManual.uploadButton").d("Upload Manual")}>
                            <IconButton
                              color="inherit"
                              onClick={() => setIsUploadModalOpen(true)}
                              sx={{ mr: 1 }}
                            >
                              <UploadFileIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {countLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {countLabel}
                          </Typography>
                        )}
                        {lastSyncLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {lastSyncLabel}
                          </Typography>
                        )}
                        <Tooltip title={intl.get("helpCenter.refreshTooltip").d("Refresh content")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={refresh}
                              disabled={loading}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    {contextError && (
                      <Alert severity="error">
                        {intl
                          .get("helpCenter.contextError")
                          .d("Could not load the Help Center.")}
                      </Alert>
                    )}

                    <DmsHelpCenterOfflineBanner
                      isOffline={isOffline}
                      isStale={isStale}
                      lastUpdated={lastUpdated}
                    />

                    <Divider />

                    <Box role="tabpanel">
                      <DmsHelpCenterManualTab
                        manuals={manuals}
                        loading={loading && manuals.length === 0}
                        error={manualError}
                        anchorId={manualAnchorId}
                        onSelectAnchor={setManualAnchorId}
                      />
                    </Box>
                  </>
                )}

                {activeTab === 1 && (
                  <Box role="tabpanel">
                    <DmsHelpCenterContactTab onSuccess={onClose} />
                  </Box>
                )}
              </Stack>
        </DmsModal.Body>
      </DmsModal>
      {havePermissions([Permissions.CAN_UPLOAD_MANUAL]) && (
        <DmsModal
          isVisible={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DmsModal.Header
            title={intl.get("DmsHelpCenterUploadManual.title").d("Help Center Manual Upload")}
            onClose={() => setIsUploadModalOpen(false)}
          />
          <DmsModal.Body>
            <DmsHelpCenterUploadManual />
          </DmsModal.Body>
        </DmsModal>
      )}
    </>
  );
};